import React from "react";
import {
  Badge,
  Box,
  Code,
  Divider,
  HStack,
  Image,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Text,
  VStack,
} from "@chakra-ui/react";
import { ExternalLinkIcon } from "@chakra-ui/icons";
import AccountsList from "./AccountsList";

const ConnectionDetailModal: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  connection: any | null;
}> = ({ isOpen, onClose, connection }) => {
  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent bg="var(--bg)" maxW="4xl">
        <ModalHeader>Connection details</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          {!connection ? (
            <Text>No details available.</Text>
          ) : (
            <VStack alignItems="flex-start" spacing={4} w="full">
              <HStack spacing={4} alignItems="center">
                {connection?.brokerage?.aws_s3_square_logo_url && (
                  <Image
                    src={connection.brokerage.aws_s3_square_logo_url}
                    alt={connection?.brokerage?.display_name || "Brokerage"}
                    boxSize="32px"
                    borderRadius="md"
                  />
                )}
                <HStack spacing={3} alignItems="center">
                  <Text fontWeight={800} fontSize="lg">
                    {connection?.brokerage?.display_name || connection?.brokerage?.name || connection?.brokerage?.slug}
                  </Text>
                  {connection?.brokerage?.slug && (
                    <Badge colorScheme="purple">{connection.brokerage.slug}</Badge>
                  )}
                  <Badge colorScheme={connection?.disabled ? "red" : "green"}>
                    {connection?.disabled ? "Disabled" : "Active"}
                  </Badge>
                </HStack>
                {connection?.brokerage?.url && (
                  <Link href={connection.brokerage.url} isExternal color="var(--accent)">
                    Website <ExternalLinkIcon mx="2px" />
                  </Link>
                )}
              </HStack>

              <SimpleGrid columns={2} spacing={3} w="full">
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Connection ID</Text>
                  <Code>{connection?.id}</Code>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Type</Text>
                  <Text>{connection?.type}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Created</Text>
                  <Text>{connection?.created_date ? new Date(connection.created_date).toLocaleString() : "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Updated</Text>
                  <Text>{connection?.updated_date ? new Date(connection.updated_date).toLocaleString() : "-"}</Text>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Allows trading</Text>
                  <Badge colorScheme={connection?.brokerage?.allows_trading ? "green" : "gray"}>
                    {connection?.brokerage?.allows_trading ? "Yes" : "No"}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Fractional units</Text>
                  <Badge colorScheme={connection?.brokerage?.allows_fractional_units ? "green" : "gray"}>
                    {connection?.brokerage?.allows_fractional_units ? "Yes" : "No"}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Real-time</Text>
                  <Badge colorScheme={connection?.brokerage?.is_real_time_connection ? "green" : "gray"}>
                    {connection?.brokerage?.is_real_time_connection ? "Yes" : "No"}
                  </Badge>
                </Box>
                <Box>
                  <Text fontSize="xs" color="var(--light-gray)">Reporting</Text>
                  <Badge colorScheme={connection?.brokerage?.has_reporting ? "green" : "gray"}>
                    {connection?.brokerage?.has_reporting ? "Yes" : "No"}
                  </Badge>
                </Box>
              </SimpleGrid>

              {Array.isArray(connection?.brokerage?.authorization_types) && connection.brokerage.authorization_types.length > 0 && (
                <Box w="full">
                  <Text fontWeight={700} mb="6px">Authorization types</Text>
                  <HStack wrap="wrap" spacing={2}>
                    {connection.brokerage.authorization_types.map((t: any, idx: number) => (
                      <Badge key={idx} colorScheme="blue">{t.type} · {t.auth_type}</Badge>
                    ))}
                  </HStack>
                </Box>
              )}

              <Divider />

              <Box w="full">
                <Text fontWeight={700} mb="6px">Accounts under this connection</Text>
                <AccountsList authorizationId={connection?.id} />
              </Box>

              {connection?.meta && (
                <Box w="full">
                  <Text fontWeight={700} mb="6px">Meta</Text>
                  <SimpleGrid columns={2} spacing={2}>
                    {Object.keys(connection.meta).map((k) => (
                      <Box key={k}>
                        <Text fontSize="xs" color="var(--light-gray)">{k}</Text>
                        <Text fontSize="sm">{String((connection.meta as any)[k])}</Text>
                      </Box>
                    ))}
                  </SimpleGrid>
                </Box>
              )}
            </VStack>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

export default ConnectionDetailModal;


